import React from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { useGenericReducer } from '../../hooks/generic-hook'
import {
  PaginationActions,
  PaginationActionType,
} from '../../hooks/pagination-reducer'
import {
  ActionUpdateTenantID,
  CommonActionType,
} from '../../hooks/common-actions'
import { createEntityQueryReducer } from '../../hooks/create-entity-pagination-reducer'
import KopiliotList from '../../components/kopiliot-list/KopiliotList'
import { useNavigate } from 'react-router-dom'
import { NotificationRecipient } from '../../shared/models/notification.model'
import { useLazyGetNotificationRecipientsQuery } from '../../store/kopiliot-api/notification-recipient'
import { NotificationRecipientQuery } from '../../store/kopiliot-api/query-type'
import {
  defaultNotificationRecipientActionsRow,
  getColumnsForTableNotificationRecipients,
} from '../../features/notifications/utils'
import AddOrEditNotificationRecipientModal from '../../features/notifications/list/AddOrEditNotificationRecipientModal'

const NotificationRecipientPage = () => {
  const user = useSelector((state: RootState) => state.currentUser)
  const navigate = useNavigate()
  // Redux Hooks
  const [getNotificationRecipients, resultGetNotificationRecipient] =
    useLazyGetNotificationRecipientsQuery()
  // State
  const [showModalDelete, setShowModalDelete] = React.useState(false)
  const [showModalCreate, setShowModalCreate] = React.useState(false)
  // Pagination
  const reducer = React.useMemo(() => {
    return createEntityQueryReducer<
      NotificationRecipientQuery,
      PaginationActions | ActionUpdateTenantID
    >({
      handleSpecificActions: {
        [CommonActionType.UPDATE_TENANT_ID]: (state, action) => {
          return { ...state, tenantID: action.payload.tenantID }
        },
      },
    })
  }, [])
  const [paginationState, dispatchPaginationAction] = useGenericReducer<
    typeof reducer,
    NotificationRecipientQuery,
    PaginationActions | ActionUpdateTenantID
  >(reducer, {
    textSearch: '',
    tenantID: undefined,
    limit: 10,
    offset: 0,
  })

  const fetchNotificationRecipient = () => {
    getNotificationRecipients({
      textSearch: paginationState.textSearch,
      offset: paginationState.offset,
      limit: paginationState.limit,
    })
  }
  const getData = () => {
    return resultGetNotificationRecipient.data
  }

  // Hooks
  React.useEffect(() => {
    fetchNotificationRecipient()
  }, [
    paginationState.offset,
    paginationState.limit,
    paginationState.textSearch,
  ])
  const isLoadingData =
    resultGetNotificationRecipient.isLoading ||
    resultGetNotificationRecipient.isFetching
  const onDelete = () => {}
  const onEdit = () => {}
  return (
    <>
      <KopiliotList
        title={'Destinataires'}
        icon={'users-viewfinder'}
        isLoading={isLoadingData}
        actionsProps={{
          onRefresh: () => {
            fetchNotificationRecipient()
          },
          onAdd: () => {
            setShowModalCreate(true)
          },
        }}
        data={getData()}
        paginationProps={{
          pageLimit: paginationState.limit,
          totalRecords: getData()?.totalRows || 0,
          onPageChanged: (page: {
            currentPage: number
            totalPages: number
            pageLimit: number
          }) => {
            dispatchPaginationAction({
              type: PaginationActionType.PAGE_CHANGE,
              payload: { ...page },
            })
          },
          onChangePageSize: (size: number) => {
            dispatchPaginationAction({
              type: PaginationActionType.PAGE_SIZE,
              payload: { pageSize: size },
            })
          },
        }}
        tableProps={{
          tableProps: {
            size: 'sm',
            className: 'mb-0 overflow-hidden table-hover cursor-pointer',
          },
          rowClassName: 'default__table-row align-middle fs-10',
          hiddenColumns: ['id'],
          headerClassName: 'bg-200 text-900 text-nowrap align-middle',
          cellClassName: 'py-2 pe-4',
          columns: getColumnsForTableNotificationRecipients(
            defaultNotificationRecipientActionsRow(onDelete, onEdit)
          ),
          onClickRow: (row) => {
            navigate(`${row.original.id?.id}`)
          },
        }}
        cardProps={{
          idAccessor: (item: NotificationRecipient) => item.id!.id,
          titleAccessor: (item: NotificationRecipient) => item.name,
          descriptionAccessor: (item: NotificationRecipient) =>
            item.description,
          linkAccessor: (item: NotificationRecipient) => `${item.id!.id}`,
          actionsRow: defaultNotificationRecipientActionsRow(onDelete, onEdit),
        }}
      />
      <AddOrEditNotificationRecipientModal
        show={showModalCreate}
        onHide={() => setShowModalCreate(false)}
      />
    </>
  )
}

export default NotificationRecipientPage
